
import { computed, defineComponent, onMounted, ref, getCurrentInstance, nextTick } from "vue-demi";
import axios from "@/plugins/http";
import { API_ROUTES } from "@/utils/API";
import { SMTasks, SMCases, SMUser, SMApp, SMNotifications } from "@/store";
import type { API_TFile } from "@/models/file"
import { EMPLOYER_ROLE, EMPLOYEE_ROLE } from "@/constants/roles";

export default defineComponent({
  name: "TaskDocumentTemplate",
  components: {
    ActionButtons: () => import("@/components/ActionContainer/ActionButtons.vue"),
    ActionContainerCard: () => import("@/components/ActionContainer/ActionContainerCard.vue"),
    UploaderPopup: () => import("@/modules/Documents/UploaderPopup.vue"),
    ParsedDocumentPopup: () => import("@/modules/Documents/ParsedDocumentPopup.vue"),
    FileView: () => import("@/modules/Documents/FileView.vue"),
    NewConfirmPopup: () => import("@/modules/Documents/NewConfirmPopup.vue"),
    PDFViewer: () => import("@/modules/Documents/PDFViewer.vue"),
  },
  setup() {
    const COMPANY_PAGE_TITLE = "Company";
    const EMPLOYEES_PAGE_TITLE = "Employees";
    const MY_PROFILE_PAGE_TITLE = "My Profile";

    const router = getCurrentInstance()?.proxy?.$router;

    const taskTemplate = computed(() => SMTasks.taskTemplate);

    const files = ref<File[]>([]);

    const savedFiles = ref<API_TFile[]>([]);

    const loading = ref(false);

    const canViewTaskActions = computed(() => {
      if (SMUser.isEmployee && SMUser.user.role !== SMTasks.user.role) return false;

      return true;
    })

    const fileType = computed(
      () => taskTemplate.value?.document_template_v2?.file_type || "default"
    );

    const isParsedFileType = computed(() => {
      return ["cos", "ielts", "pte"].includes(fileType.value);
    });

    const uploadButtonLabel = computed(() => {
      if (!files.value.length) return "Upload";

      return isParsedFileType.value ? "Reupload" : "Upload more";
    })

    const isCase = computed(() => taskTemplate.value?.product_type === "case")

    onMounted(async () => {
      const directoryId = taskTemplate.value.directory_id;

      if (!directoryId) return; // note: directory_id exists only for completed tasks

      try {
        const { data } = await axios.get(API_ROUTES.DOCUMENTS_DIRECTORY_FILES(directoryId));

        if (!data) return;

        savedFiles.value = data;
      } finally {
        // this.loading = false;
      }
    });

    const parsedContent = ref(null);

    // Upload Popup
    const isUploadPopupOpen = ref(false);

    function toggleUploadPopup(value: boolean) {
      isUploadPopupOpen.value = value;
    }

    const onFiles = (payload: File[]) => {
      files.value = payload;

      isUploadPopupOpen.value = false;
    };

    // Confirm Popup
    const isConfirmPopupOpen = ref(false);

    function toggleConfirmPopup(value: boolean) {
      isConfirmPopupOpen.value = value;
    }

    function onConfirmOk() {
      toggleConfirmPopup(false);
    }

    const uploaderPopupKey = ref(0);

    const isParsedDocumentPopup = ref(false);

    function toggleParsedDocumentPopup(value: boolean) {
      isParsedDocumentPopup.value = value;

      uploaderPopupKey.value += 1;
    }

    function onConfirmCancel() {
      toggleConfirmPopup(false);

      toggleUploadPopup(false);

      toggleParsedDocumentPopup(false);

      files.value = [];

      parsedContent.value = null;

      uploaderPopupKey.value += 1;
    }

    function onUploaderCancel(hasChange: boolean) {
      hasChange ? toggleUploadPopup(false) : toggleConfirmPopup(true);
    }

    // Parsed Popup
    function openParsedDocumentPopup(payload: File[]) {
      files.value = payload;

      isUploadPopupOpen.value = false;

      isParsedDocumentPopup.value = true;
    }

    const onParsedDataOk = (payload: any) => {
      parsedContent.value = payload;
    };

    const onParsedDataCancel = () => {
      toggleConfirmPopup(true);
    };

    // PDF Preview Popup
    const fileIdViewer = ref(0);
    const pdfViewerKey = ref(0);
    const pdfViewerLink = ref("");
    const pdfViewerName = ref("");
    const isPdfViewerOpen = ref(false);

    function togglePdfViewer(value: boolean) {
      if (!value) {
        fileIdViewer.value = 0;
        pdfViewerLink.value = "";
        pdfViewerName.value = "";
        pdfViewerKey.value += 1;
      }

      isPdfViewerOpen.value = value;
    }

    // Page
    function setPageSectionName() {
      const targetUser = SMTasks.taskTemplate?.target_user;

      if (!targetUser) return;

      const { first_name, last_name } = targetUser;

      const name = `${first_name} ${last_name}`;

      if (!name) return;

      SMApp.setEmployeeName(name);

      const sectionName = ` > ${name}`;

      SMApp.setPageSection(sectionName);
    }

    async function goToDocuments() {
      if (!savedFiles.value.length) return;

      const s3Path = savedFiles.value[0]?.s_3_path || "";
      if (!s3Path) return;

      const path = s3Path.replace(/\/[^/]*$/, "");

      let url = "";

      // TO DO: refactor
      let object_type = "";
      let object_id = 0;

      if (taskTemplate.value.product_type == "case") {
        object_type = 'case';
        object_id = taskTemplate.value.product_type_id;
      } else if (taskTemplate.value.product_type == "compliance") {
        if (taskTemplate.value.target_user.ID === 0) {
          if (taskTemplate.value.user.role == 'employer') {
            object_type = 'company';
            object_id = taskTemplate.value.company_id;
          } else if (taskTemplate.value.user.role == 'employee') {
            object_type = 'employee'
            object_id = taskTemplate.value.user_id
          }
        } else {
          object_type = 'employee';
          object_id = taskTemplate.value.target_user_id;
        }
      }
      //
      console.log(object_id);

      try {
        if (object_type === "case") {
          url = `/cases/${SMCases.currentCaseId}/documents/${SMCases.documentId}`;
        } else if (SMUser.isEmployer) {
          if (
            SMTasks.taskTemplate?.target_user.role === EMPLOYEE_ROLE ||
            SMTasks.taskTemplate?.user_role === EMPLOYEE_ROLE
          ) {
            const id = SMTasks.taskTemplate?.target_user?.ID || SMTasks.taskTemplate?.user_id;
            if (!id) return console.log("No employee ID");

            url = `${SMUser.basePath}/employees/${id}/documents`;
            SMApp.setPageTitle(EMPLOYEES_PAGE_TITLE);
          } else {
            url = `${SMUser.basePath}/company/documents`;
            SMApp.setPageTitle(COMPANY_PAGE_TITLE);
          }
        } else if (SMUser.isEmployee) {
          url = `${SMUser.basePath}/profile/documents`;
          SMApp.setPageTitle(MY_PROFILE_PAGE_TITLE);
        } else if (SMUser.isExpert) {
          if (router.currentRoute.params.company_id) {
            if (
              SMTasks.taskTemplate?.target_user_id &&
              SMTasks.taskTemplate?.target_user.role !== EMPLOYER_ROLE
            ) {
              const { company_id } = router.currentRoute.params;
              url = `${SMUser.basePath}/companies/${company_id}/employees/${SMTasks.taskTemplate.target_user_id}/documents`;
              SMApp.setPageTitle(EMPLOYEES_PAGE_TITLE);
              setPageSectionName();
            } else {
              const { company_id } = router.currentRoute.params;
              url = `${SMUser.basePath}/companies/${company_id}/documents`;
              SMApp.setPageTitle(COMPANY_PAGE_TITLE);
            }
          } else {
            console.log("No company ID");
            return;
          }
        } else {
          console.log("Current role has no access!");
          return;
        }

        if (path) url += `?path=${encodeURIComponent(path)}`;

        await router.push(url);
      } catch (error) {
        console.error("Error navigating to documents:", error);
      }
    }

    const additionalData = ref(null);

    function setAdditionalData(payload) {
      additionalData.value = payload
    };

    const date = ref(null);

    function setDate(payload) {
      date.value = payload;
    }

    async function onParsedDocumentReupload() {
      files.value = [];
      parsedContent.value = null;
      additionalData.value = null;
      date.value = null

      toggleParsedDocumentPopup(false);

      uploaderPopupKey.value += 1;

      await nextTick();

      toggleUploadPopup(true);
    }

    async function saveDocuments() {
      // TO DO: refactor
      let object_type = "";
      let object_id = 0;

      if (taskTemplate.value.product_type == "case") {
        object_type = 'case';
        object_id = taskTemplate.value.product_type_id;
      } else if (taskTemplate.value.product_type == "compliance") {
        if (taskTemplate.value.target_user.ID === 0) {
          if (taskTemplate.value.user.role == 'employer') {
            object_type = 'company';
            object_id = taskTemplate.value.company_id;
          } else if (taskTemplate.value.user.role == 'employee') {
            object_type = 'employee'
            object_id = taskTemplate.value.user_id
          }
        } else {
          object_type = 'employee';
          object_id = taskTemplate.value.target_user_id;
        }
      }
      //

      if (!object_type) return console.log("no object type");
      if (!object_id) return console.log("no object id");

      const dtv2 = taskTemplate.value.document_template_v2_id;
      const taskId = taskTemplate.value.id;

      if (!dtv2) return console.log("no doc template id");
      if (!taskId) return console.log("no task id");

      try {
        loading.value = true;

        const formData = new FormData();

        files.value.forEach((file) => {
          formData.append("file", file);
        });
        formData.append("task_id", `${taskId}`);
        formData.append("object_type", object_type);
        formData.append("object_id", `${object_id}`);
        formData.append("document_template_id", `${dtv2}`);

        if (additionalData.value)
          formData.append("additional_data", JSON.stringify(additionalData.value));

        if (parsedContent.value) {
          formData.append("document_data", JSON.stringify(parsedContent.value));
        } else if (date.value) {
          formData.append("document_data", JSON.stringify({ date: date.value }));
        }

        const url = API_ROUTES.DOCUMENTS_UPLOAD;

        const { data } = await axios.post(url, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        savedFiles.value = data.Files;

        SMNotifications.fetchNotificationsStatus();
      } catch (error) {
        parsedContent.value = null;
      } finally {
        files.value = [];

        loading.value = false;
      }
    }

    const isDocumentSaved = computed(() => !!savedFiles.value.length);

    function onFileView(fileId: number) {
      if (!fileId) return;

      fileIdViewer.value = fileId;

      pdfViewerKey.value += 1;

      togglePdfViewer(true);
    }

    function onFileDelete(index: number) {
      if (index > -1 && index < files.value.length) {
        files.value.splice(index, 1);
      }
    }

    function onFileDownload(index: number) {
      const file = files.value[index];

      if (!file) {
        console.log("file not found at index:", index);
        return;
      }

      const url = URL.createObjectURL(file);
      const a = document.createElement("a");

      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      URL.revokeObjectURL(url);
    }

    async function blobToDataURL(blob: Blob): Promise<string> {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(blob);
      });
    }

    async function onFilePreview(index: number) {
      const file = files.value[index];

      if (!file) {
        console.log("file not found at index:", index);
        return;
      }

      if (file.type !== "application/pdf") {
        return;
      }

      const fileUrl = await blobToDataURL(file);


      pdfViewerLink.value = fileUrl;
      pdfViewerName.value = file.name;

      pdfViewerKey.value += 1;

      togglePdfViewer(true);
    }

    const isSaveDisabled = computed(() => {
      if (!files.value.length) return true;

      if (isDocumentSaved.value) return true;

      return false;
    });

    return {
      isUploadPopupOpen,
      goToDocuments,
      loading,
      saveDocuments,
      onFiles,
      savedFiles,
      files,
      isParsedDocumentPopup,
      openParsedDocumentPopup,
      toggleUploadPopup,
      toggleParsedDocumentPopup,
      onParsedDataOk,
      onParsedDataCancel,
      toggleConfirmPopup,
      isConfirmPopupOpen,
      isDocumentSaved,
      uploadButtonLabel,
      onFileView,
      onFileDelete,
      onFilePreview,
      onFileDownload,
      isPdfViewerOpen,
      togglePdfViewer,
      fileIdViewer,
      pdfViewerKey,
      pdfViewerLink,
      pdfViewerName,
      onUploaderCancel,
      onConfirmOk,
      onConfirmCancel,
      isSaveDisabled,
      setAdditionalData,
      setDate,
      uploaderPopupKey,
      onParsedDocumentReupload,
      canViewTaskActions,
      isCase,
    };
  },
});
